import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import { Chip, Expander, Text } from '~/components/core';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import {
    Strategy,
    StrategyExitCondition,
    StrategyIndicatorInstance,
    StrategyInput,
    StrategyInputNode,
    StrategyNodeAttributeTemplate,
    StrategyNodeTemplate,
    StrategyResponseData,
} from '~/api-services/AIStrategyBuilderService/types';
import { hasOptimalResult, periodLabel } from '~/constants/strategyConstants';
import { useAppSelector, useStyles } from '~/hooks';
import i18n from '~/i18n';
import { accountDetailsSelector, symbolsSelector } from '~/state/selectors';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { createNodeDescriber, formatAttrValue, isRange } from '../../utils/describe';

type Props = {
    strategy: Strategy;
};

type Styles = ReturnType<typeof stylesGenerator>;

// Read-only render of template-defined fields, filtering hidden ones (and name/type at the
// node's top level), recursing into nested groups — ported from the PoC ViewStrategyNodes.
const ReadonlyFields = ({
    fields,
    value,
    styles,
    topLevel,
}: {
    fields: StrategyNodeAttributeTemplate[];
    value: Record<string, unknown> | undefined;
    styles: Styles;
    topLevel?: boolean;
}) => {
    const visible = fields.filter((f) => !f.hidden && (!topLevel || (f.name !== 'name' && f.name !== 'type')));
    return (
        <>
            {visible.map((f) => {
                const v = value ? value[f.name] : undefined;
                if (f.fields && f.fields.length > 0 && v && typeof v === 'object' && !isRange(v)) {
                    const nested = v as Record<string, unknown>;
                    return (
                        <View key={f.name} style={styles.attrGroup}>
                            <Text noTranslate style={styles.attrGroupLabel}>
                                {f.name}
                            </Text>
                            <ReadonlyFields fields={f.fields} value={nested} styles={styles} />
                        </View>
                    );
                }
                return (
                    <View key={f.name} style={styles.attrRow}>
                        <Text noTranslate style={styles.attrLabel}>
                            {f.name}
                        </Text>
                        <Text noTranslate style={styles.attrValue}>
                            {formatAttrValue(v)}
                        </Text>
                    </View>
                );
            })}
        </>
    );
};

// Compact one-liner of an indicator's visible parameters ("period 4 – 16 · deviation 2") for the
// collapsed expander subtitle — this is what makes the Submitted ↔ Optimized toggle visibly
// different (search ranges vs concrete tuned values) without opening every indicator.
const paramsSummary = (fields: StrategyNodeAttributeTemplate[], value: Record<string, unknown> | undefined): string => {
    const parts: string[] = [];
    const walk = (fs: StrategyNodeAttributeTemplate[], val: Record<string, unknown> | undefined) => {
        for (const f of fs) {
            if (f.hidden || f.name === 'name' || f.name === 'type') {
                continue;
            }
            const v = val ? val[f.name] : undefined;
            if (f.fields && f.fields.length > 0 && v && typeof v === 'object' && !isRange(v)) {
                walk(f.fields, v as Record<string, unknown>);
            } else {
                parts.push(`${f.name} ${formatAttrValue(v)}`);
            }
        }
    };
    walk(fields, value);
    return parts.slice(0, 3).join(' · ') + (parts.length > 3 ? ' · …' : '');
};

const SummaryRow = ({ label, value, styles }: { label: string; value: string; styles: Styles }) => (
    <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>{label}</Text>
        <Text noTranslate style={styles.summaryValue}>
            {value}
        </Text>
    </View>
);

/**
 * Read-only configuration view: market + trade parameters from the CLOUD node, rules and
 * optimisations described in the wizard's language, and — for accounts with
 * `strategyRawEnabled` — the raw JSON behind an expander.
 * When an optimized result exists, a Submitted ↔ Optimized toggle re-renders the same view
 * over `response.optimal_strategy` (concrete tuned values instead of search ranges).
 */
export default function SetupTab({ strategy }: Props) {
    const styles = useStyles(stylesGenerator);
    const symbolsMap = useAppSelector(symbolsSelector);
    // Opt-in per account: the raw blobs are a support/debug aid, not part of the normal UI.
    const rawEnabled = useAppSelector(accountDetailsSelector)?.strategyRawEnabled === true;
    const [variant, setVariant] = useState<'input' | 'optimal'>('input');
    const [indicatorInstances, setIndicatorInstances] = useState<Record<string, StrategyIndicatorInstance>>({});
    const [nodeTemplates, setNodeTemplates] = useState<Record<string, StrategyNodeTemplate>>({});

    // Metadata is best-effort: without it, labels/fields fall back to the nodes' own keys.
    useEffect(() => {
        Promise.all([
            AIStrategyBuilderService.getStrategyIndicatorInstances().catch(() => null),
            AIStrategyBuilderService.getStrategyNodeTemplates().catch(() => null),
        ]).then(([instRes, tmplRes]) => {
            if (instRes?.data?.status && instRes.data.returnData) {
                setIndicatorInstances(instRes.data.returnData);
            }
            if (tmplRes?.data?.status && tmplRes.data.returnData) {
                setNodeTemplates(tmplRes.data.returnData);
            }
        });
    }, []);

    const input = (strategy.input ?? undefined) as StrategyInput | undefined;
    const response = (strategy.response ?? undefined) as StrategyResponseData | undefined;
    const optimalNodes = response?.optimal_strategy ?? [];
    const canToggle = hasOptimalResult(strategy.status) && optimalNodes.length > 0;
    const showOptimal = variant === 'optimal' && canToggle;

    const instrumentRef = showOptimal ? response?.instrument : input?.instrument;

    const nodes = useMemo<StrategyInputNode[]>(() => {
        const resp = (strategy.response ?? undefined) as StrategyResponseData | undefined;
        const inp = (strategy.input ?? undefined) as StrategyInput | undefined;
        return showOptimal ? resp?.optimal_strategy ?? [] : inp?.strategy ?? [];
    }, [strategy, showOptimal]);

    const describer = useMemo(
        () => createNodeDescriber(nodes, indicatorInstances, nodeTemplates),
        [nodes, indicatorInstances, nodeTemplates]
    );

    const instrumentCode = instrumentRef?.code ?? strategy.instrument_code ?? '';
    const period = instrumentRef?.period ?? strategy.instrument_period ?? '';
    const instrumentName = symbolsMap[instrumentCode]?.nameTranslated ?? instrumentCode ?? '—';

    const container = describer.container;
    const exit = (container?.exitCondition ?? {}) as Partial<StrategyExitCondition>;
    const quantityRaw = Number(container?.quantity ?? NaN);
    const hasContainer = !!container && Number.isFinite(quantityRaw);

    if (nodes.length === 0) {
        return (
            <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>ai-strategy-setup-empty</Text>
            </View>
        );
    }

    const renderGroup = (
        key: string,
        title: string,
        group: { indicators: StrategyInputNode[]; connections: StrategyInputNode[] }
    ) => (
        <View key={key} style={styles.ruleBlock}>
            <Text noTranslate fontType="LATO_BOLD" style={styles.ruleTitle}>
                {title}
            </Text>
            {group.connections.map((conn, i) => (
                <Text noTranslate key={`conn-${i}`} style={styles.connectionText}>
                    {describer.describeConnection(conn)}
                </Text>
            ))}
            {group.indicators.map((node, i) => {
                const fields = describer.fieldsForNode(node);
                const visibleFields = fields
                    ? fields.filter((f) => !f.hidden && f.name !== 'name' && f.name !== 'type')
                    : [];
                if (visibleFields.length === 0) {
                    return null;
                }
                return (
                    <Expander
                        key={`node-${i}`}
                        title={describer.nodeLabel(node)}
                        subtitle={paramsSummary(visibleFields, node as Record<string, unknown>)}
                    >
                        <ReadonlyFields
                            fields={visibleFields}
                            value={node as Record<string, unknown>}
                            styles={styles}
                            topLevel
                        />
                    </Expander>
                );
            })}
        </View>
    );

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content}>
            {canToggle ? (
                <>
                    <View style={styles.toggleRow}>
                        <Chip
                            label="ai-strategy-setup-submitted"
                            selected={!showOptimal}
                            onPress={() => setVariant('input')}
                            containerStyle={styles.toggleChip}
                        />
                        <Chip
                            label="ai-strategy-setup-optimized"
                            selected={showOptimal}
                            onPress={() => setVariant('optimal')}
                            containerStyle={styles.toggleChip}
                        />
                    </View>
                    <Text style={styles.variantHint}>ai-strategy-setup-variant-hint</Text>
                </>
            ) : null}

            <View style={styles.summaryBlock}>
                <SummaryRow label="instrument" value={instrumentName} styles={styles} />
                <SummaryRow label="period" value={period ? periodLabel(period) : '—'} styles={styles} />
                {hasContainer ? (
                    <>
                        <SummaryRow
                            label="side"
                            value={quantityRaw >= 0 ? i18n.t('buy') : i18n.t('sell')}
                            styles={styles}
                        />
                        <SummaryRow label="quantity" value={String(Math.abs(quantityRaw))} styles={styles} />
                        <SummaryRow label="take-profit" value={formatAttrValue(exit.takeProfit)} styles={styles} />
                        <SummaryRow label="stop-loss" value={formatAttrValue(exit.stopLoss)} styles={styles} />
                        {/* The backend derives the fund server-side (2026-07-27) and still stores
                            it in the CLOUD node regardless — shown here for reference. */}
                        {container?.fund != null ? (
                            <SummaryRow label="fund" value={formatAttrValue(container.fund)} styles={styles} />
                        ) : null}
                    </>
                ) : null}
            </View>

            {describer.ruleGroups.length > 0 ? (
                <>
                    <Text fontType="LATO_BOLD" style={styles.sectionTitle}>
                        ai-strategy-setup-rules
                    </Text>
                    {describer.ruleGroups.map((group) =>
                        renderGroup(
                            `rule-${group.num}`,
                            describer.ruleGroups.length > 1 ? `${i18n.t('ai-strategy-setup-rule')} ${group.num}` : '',
                            group
                        )
                    )}
                </>
            ) : null}

            {describer.optimisationGroups.length > 0 ? (
                <>
                    <Text fontType="LATO_BOLD" style={styles.sectionTitle}>
                        ai-strategy-setup-optimisations
                    </Text>
                    {describer.optimisationGroups.map((group) => renderGroup(`opt-${group.code}`, group.code, group))}
                </>
            ) : null}

            {rawEnabled ? (
                <View style={styles.rawBlock}>
                    <Expander title="ai-strategy-raw-json">
                        {/* Mirrors the Submitted ↔ Optimized toggle above: the submitted `input`
                            or the engine's `response`, plus the two contract blobs when present. */}
                        <Text noTranslate style={styles.rawLabel}>
                            {showOptimal ? 'response' : 'input'}
                        </Text>
                        <Text noTranslate style={styles.rawJson}>
                            {JSON.stringify(showOptimal ? response : input, null, 2)}
                        </Text>
                        {strategy.frontend_json ? (
                            <>
                                <Text noTranslate style={styles.rawLabel}>
                                    frontend_json
                                </Text>
                                <Text noTranslate style={styles.rawJson}>
                                    {JSON.stringify(strategy.frontend_json, null, 2)}
                                </Text>
                            </>
                        ) : null}
                        {strategy.backend_json ? (
                            <>
                                <Text noTranslate style={styles.rawLabel}>
                                    backend_json
                                </Text>
                                <Text noTranslate style={styles.rawJson}>
                                    {JSON.stringify(strategy.backend_json, null, 2)}
                                </Text>
                            </>
                        ) : null}
                    </Expander>
                </View>
            ) : null}
        </ScrollView>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { padding: scaleSize(12) },
        emptyContainer: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: scaleSize(16),
        },
        emptyText: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_S,
            textAlign: 'center',
        },
        toggleRow: {
            flexDirection: 'row',
            marginBottom: scaleSize(10),
        },
        toggleChip: {
            marginRight: scaleSize(8),
        },
        variantHint: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginBottom: scaleSize(10),
        },
        summaryBlock: {
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(8),
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(6),
        },
        summaryRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingVertical: scaleSize(5),
        },
        summaryLabel: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_S,
        },
        summaryValue: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            flex: 1,
            textAlign: 'right',
            marginLeft: scaleSize(8),
        },
        sectionTitle: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            marginTop: scaleSize(14),
            marginBottom: scaleSize(6),
        },
        ruleBlock: {
            marginBottom: scaleSize(6),
        },
        ruleTitle: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginBottom: scaleSize(4),
        },
        connectionText: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(6),
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(8),
            marginBottom: scaleSize(6),
        },
        attrGroup: {
            marginTop: scaleSize(4),
        },
        attrGroupLabel: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginBottom: scaleSize(2),
        },
        attrRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingVertical: scaleSize(3),
        },
        attrLabel: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
        },
        attrValue: {
            color: theme.text,
            fontSize: FONT_SIZE_XS,
        },
        rawBlock: {
            marginTop: scaleSize(14),
        },
        rawLabel: {
            color: theme.text,
            fontSize: FONT_SIZE_XS,
            marginTop: scaleSize(6),
            marginBottom: scaleSize(2),
        },
        rawJson: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            fontFamily: 'monospace',
        },
    });
